import axios, { AxiosError, AxiosProgressEvent, GenericAbortSignal, Method, ParamsSerializerOptions } from 'axios';

import { SERVER_ERROR_MESSAGE } from '@/shared/api/errors';

const GENERIC_AXIOS_ERRORS = {
  badOptionError: 'ERR_BAD_OPTION',
  badOptionValueError: 'ERR_BAD_OPTION_VALUE',
  badRequestError: 'ERR_BAD_REQUEST',
  badResponseError: 'ERR_BAD_RESPONSE',
  connAbortedError: 'ECONNABORTED',
  deprecatedError: 'ERR_DEPRECATED',
  invalidURLError: 'ERR_INVALID_URL',
  networkError: 'ERR_NETWORK',
  notSupportError: 'ERR_NOT_SUPPORT',
  timeoutError: 'ETIMEDOUT',
  tooManyRedirectsError: 'ERR_FR_TOO_MANY_REDIRECTS',
  canceledError: 'ERR_CANCELED',
} as const;

type TRequestClosure<ResponseData, Params extends object = object, Data = object> = {
  adapter?: (data: ResponseData) => ResponseData;
  data?: Data;
  params?: Params;
  paramsSerializer?: ParamsSerializerOptions;
  signal?: GenericAbortSignal;
  type?: string;
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
};

const requestClosure =
  (method: Method) =>
  <ResponseData, Params extends object = object, Data = object>(
    url: string,
    {
      adapter,
      data,
      params,
      paramsSerializer,
      signal,
      type = 'application/json',
      onUploadProgress,
    }: TRequestClosure<ResponseData, Params, Data> = {},
  ) => {
    const headers = {
      ...(method === 'post' || method === 'put' || method === 'patch' ? { 'Content-Type': type } : {}),
    };

    return new Promise<ResponseData>((resolve, reject) => {
      axios
        .request<ResponseData>({
          data,
          headers,
          method,
          params,
          paramsSerializer,
          signal,
          url,
          onUploadProgress,
        })
        .then((response) => adapter?.(response.data) ?? response.data)
        .then((response) => resolve(response))
        .catch((error: AxiosError) => {
          if (error.response) {
            const { data: errorPayload, status, statusText } = error.response;
            reject({ data: errorPayload, status, statusText });
            return;
          }

          if (!error.response && error.request && error.code === GENERIC_AXIOS_ERRORS.networkError) {
            reject({ data: null, status: 504, statusText: 'Gateway Timeout' });
            return;
          }

          // Подстраховка: мы не ожидаем, что попадаем в этот reject
          // когда-либо, но если это случится то выбросим стандартную ошибку
          // и showErrorAlert успешно обработает ее
          reject(new Error(SERVER_ERROR_MESSAGE));
        });
    });
  };
export const post = requestClosure('post');
export const get = requestClosure('get');
export const put = requestClosure('put');
export const patch = requestClosure('patch');
export const del = requestClosure('delete');
